import { useState, useEffect } from "react";

const API = process.env.REACT_APP_API_URL || "";

const STATUS_META = {
  ok:          { color: "#1D9E75", bg: "#1D9E7522", label: "OK",          icon: "\u2713" },
  warning:     { color: "#BA7517", bg: "#BA751722", label: "WARNING",     icon: "\u26a0" },
  error:       { color: "#E24B4A", bg: "#E24B4A22", label: "ERROR",       icon: "\u2717" },
  unavailable: { color: "#6B7280", bg: "#6B728022", label: "UNAVAILABLE", icon: "\u2014" },
};

const fmtTime = (iso) => {
  if (!iso) return "Never";
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit", timeZoneName: "short"
  });
};

const fmtHours = (h) => {
  if (h >= 9999) return "Never run";
  if (h < 1) return `${Math.round(h * 60)}m ago`;
  if (h < 24) return `${Math.round(h)}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.warning;
  return (
    <span style={{
      fontSize: 10, color: m.color, background: m.bg,
      padding: "2px 8px", borderRadius: 10, fontWeight: 600,
      display: "inline-flex", alignItems: "center", gap: 4
    }}>
      {m.icon} {m.label}
    </span>
  );
};

const Section = ({ title, status, children }) => {
  const m = STATUS_META[status] || STATUS_META.warning;
  return (
    <div style={{
      background: "#12121f",
      border: "1px solid #2a2a3e",
      borderLeft: `3px solid ${m.color}`,
      borderRadius: 8, padding: "16px 20px", marginBottom: 12
    }}>
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#e8e6de" }}>
          {title}
        </div>
        <StatusBadge status={status} />
      </div>
      {children}
    </div>
  );
};

const Row = ({ label, value, status, sub }) => {
  const m = status ? (STATUS_META[status] || STATUS_META.warning) : null;
  return (
    <div style={{ display: "flex", justifyContent: "space-between",
                  alignItems: "flex-start", padding: "5px 0",
                  borderBottom: "1px solid #1a1a2e" }}>
      <div>
        <div style={{ fontSize: 12, color: "#9a99a2" }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: "#5f5e6e" }}>{sub}</div>}
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontSize: 12, color: m ? m.color : "#e2e0d8" }}>
          {value}
        </div>
        {status && <StatusBadge status={status} />}
      </div>
    </div>
  );
};

export default function SystemHealth() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = () => {
    fetch(`${API}/health`)
      .then(r => r.json())
      .then(d => {
        setHealth(d);
        setLastRefresh(new Date());
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, []);

  if (loading) return (
    <div style={{ padding: 48, color: "#9a99a2" }}>
      Loading system health...
    </div>
  );

  if (!health) return (
    <div style={{ padding: 48, color: "#E24B4A" }}>
      Health endpoint unavailable
    </div>
  );

  const c = health.components;
  const overall = STATUS_META[health.overall] || STATUS_META.warning;

  return (
    <div style={{ minHeight: "100vh", background: "#0d0d1a", color: "#e2e0d8",
                  fontFamily: "'Inter', sans-serif", padding: "24px 32px" }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "flex-end", marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 11, color: "#5f5e6e", letterSpacing: 2,
                        textTransform: "uppercase", marginBottom: 4 }}>
            Axiom Intelligence
          </div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600,
                        color: "#e8e6de" }}>
            System Health
          </h1>
          <div style={{ fontSize: 12, color: "#5f5e6e", marginTop: 4 }}>
            Auto-refreshes every 60 seconds ·{" "}
            {lastRefresh ? `Last: ${lastRefresh.toLocaleTimeString()}` : ""}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{
            fontSize: 28, fontWeight: 700, color: overall.color,
            marginBottom: 4
          }}>
            {overall.icon} {overall.label}
          </div>
          <div style={{ fontSize: 11, color: "#5f5e6e" }}>
            Overall system status
          </div>
        </div>
      </div>

      {/* Ingest Pipeline */}
      <Section title="Data Ingest Pipeline" status={c.ingest?.status}>
        {Object.entries(c.ingest?.sources || {}).map(([key, src]) => (
          <Row
            key={key}
            label={key.replace(/_/g, " ")}
            value={`${src.record_count?.toLocaleString()} records \u00b7 ${fmtHours(src.hours_ago)}`}
            status={src.status}
            sub={fmtTime(src.last_run)}
          />
        ))}
      </Section>

      {/* CORR-004 */}
      <Section title="CORR-004 Correlation Engine"
               status={c.corr004?.status}>
        {c.corr004?.status === "unavailable" ? (
          <div style={{ fontSize: 12, color: "#6B7280" }}>
            Table not yet on production DB
          </div>
        ) : (
          <Row
            label="Last correlation run"
            value={`${c.corr004?.score_count ?? 0} scores \u00b7 ${fmtHours(c.corr004?.hours_ago)}`}
            status={c.corr004?.status}
            sub={`${fmtTime(c.corr004?.last_run)} \u00b7 Schedule: ${c.corr004?.schedule || "\u2014"}`}
          />
        )}
      </Section>

      {/* Activity Analysis */}
      <Section title="Phase Activity Analysis"
               status={c.activity_analysis?.status}>
        {c.activity_analysis?.status === "unavailable" ? (
          <div style={{ fontSize: 12, color: "#6B7280" }}>
            Table not yet on production DB
          </div>
        ) : (
          <Row
            label="Last narrative generation"
            value={`${c.activity_analysis?.narrative_count ?? 0} narratives \u00b7 ${fmtHours(c.activity_analysis?.hours_ago)}`}
            status={c.activity_analysis?.status}
            sub={`${fmtTime(c.activity_analysis?.last_run)} \u00b7 Schedule: ${c.activity_analysis?.schedule || "\u2014"}`}
          />
        )}
      </Section>

      {/* Briefings */}
      <Section title="Briefing PDFs" status={c.briefings?.status}>
        <Row
          label="Portfolio briefing"
          value={`${c.briefings?.portfolio?.count} files \u00b7 ${fmtHours(c.briefings?.portfolio?.hours_ago)}`}
          status={c.briefings?.portfolio?.status}
          sub={`${fmtTime(c.briefings?.portfolio?.last_run)} \u00b7 Schedule: ${c.briefings?.portfolio?.schedule}`}
        />
        <Row
          label="Site briefings"
          value={`${c.briefings?.site?.count} files \u00b7 ${fmtHours(c.briefings?.site?.hours_ago)}`}
          status={c.briefings?.site?.status}
          sub={`${fmtTime(c.briefings?.site?.last_run)} \u00b7 Schedule: ${c.briefings?.site?.schedule}`}
        />
      </Section>

      {/* Site Scores */}
      <Section title="Portfolio Site Scores"
               status={c.site_scores?.status}>
        {c.site_scores?.status === "unavailable" ? (
          <div style={{ fontSize: 12, color: "#6B7280" }}>
            Table not yet on production DB
          </div>
        ) : Object.keys(c.site_scores?.sites || {}).length === 0 ? (
          <div style={{ fontSize: 12, color: "#6B7280" }}>
            No site scores computed yet. Run CORR-004 to populate.
          </div>
        ) : (
          Object.entries(c.site_scores.sites).map(([code, site]) => {
            const qd = site.scores?.queue_delay;
            const sr = site.scores?.site_risk;
            const pr = site.scores?.procurement_risk;
            return (
              <Row
                key={code}
                label={site.name || code}
                value={
                  `Q:${qd != null ? Math.round(qd) : "\u2014"} ` +
                  `S:${sr != null ? Math.round(sr) : "\u2014"} ` +
                  `P:${pr != null ? Math.round(pr) : "\u2014"} \u00b7 ` +
                  fmtHours(site.hours_ago)
                }
                status={site.status}
                sub={fmtTime(site.last_updated)}
              />
            );
          })
        )}
      </Section>

      {/* Signals Screener */}
      <Section title="Trading Signals Screener"
               status={c.signals?.status}>
        {c.signals?.status === "unavailable" ? (
          <div style={{ fontSize: 12, color: "#6B7280" }}>
            Table not yet on production DB
          </div>
        ) : (
          <Row
            label="Last screener run"
            value={`${c.signals?.score_count ?? 0} scores \u00b7 ${c.signals?.run_count ?? 0} runs \u00b7 ${fmtHours(c.signals?.hours_ago)}`}
            status={c.signals?.status}
            sub={`${fmtTime(c.signals?.last_run)} \u00b7 Schedule: ${c.signals?.schedule || "\u2014"}`}
          />
        )}
      </Section>

    </div>
  );
}
